/**
 * CompactOfflineButton - Compact button for offline downloads
 *
 * Features:
 * - Icon-only or icon+text variants
 * - Shows offline status (available, partial, not downloaded)
 * - Integrates with useOfflineBook hook
 * - Opens ChapterSelectionModal for DRM books
 * - Touch-friendly (44x44px minimum)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, CheckCircle, Loader2, WifiOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useOfflineBook } from '../../hooks/useOfflineBook';
import { useBookMetadata } from '../../hooks/useBooks';
import { ChapterSelectionModal } from './ChapterSelectionModal';
import { DownloadProgressModal } from './DownloadProgressModal';
import type { ChapterSelectionInfo } from '../../types/offline';

export interface CompactOfflineButtonProps {
  bookId: string;
  variant?: 'icon' | 'text'; // icon-only or icon+text
  size?: 'sm' | 'md'; // sm for header, md for cards
  className?: string;
  onClick?: (e: React.MouseEvent) => void; // Custom click handler (optional)
}

export function CompactOfflineButton({
  bookId,
  variant = 'icon',
  size = 'md',
  className = '',
  onClick,
}: CompactOfflineButtonProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.sub || '';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chapterSelectionInfo, setChapterSelectionInfo] = useState<ChapterSelectionInfo[]>([]);

  // Fetch book metadata
  const { data: bookMetadata, isLoading: isLoadingMetadata } = useBookMetadata(bookId);

  // Use offline book hook
  const {
    isAvailableOffline,
    isDownloading,
    downloadProgress,
    downloadedChapterCount,
    availableChapterCount,
    isDrmEnabled,
    quotaExceeded,
    downloadSelectedChapters,
    downloadAllChapters,
    getChapterSelectionInfo,
  } = useOfflineBook({
    bookId,
    userId,
    bookMetadata: bookMetadata,
    autoCheckAvailability: true,
  });

  // Auto-close modal when download starts
  useEffect(() => {
    if (isDownloading) {
      setIsModalOpen(false);
    }
  }, [isDownloading]);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (onClick) {
      onClick(e);
      return;
    }

    if (!bookMetadata || isDownloading) return;

    if (isDrmEnabled) {
      // DRM enabled - show chapter selection modal
      const info = await getChapterSelectionInfo();
      setChapterSelectionInfo(info);
      setIsModalOpen(true);
    } else {
      // No DRM - download all chapters directly
      await downloadAllChapters();
    }
  };

  const handleChapterSelection = async (selectedChapterIndices: number[]) => {
    setIsModalOpen(false);
    await downloadSelectedChapters(selectedChapterIndices);
  };

  if (!user || isLoadingMetadata || !bookMetadata) {
    return null; // Hide if not logged in or loading
  }

  const totalChapters = bookMetadata.chapters.length;
  const isFullyDownloaded = downloadedChapterCount === totalChapters;
  const isPartiallyDownloaded = downloadedChapterCount > 0 && downloadedChapterCount < totalChapters;

  // Size classes
  const sizeClasses = {
    sm: 'w-10 h-10', // 40px for header
    md: 'w-11 h-11', // 44px for cards
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isDownloading || (isDrmEnabled && quotaExceeded && !isAvailableOffline)}
        className={`
          ${variant === 'icon' ? sizeClasses[size] : 'px-3 py-2 min-h-[44px]'}
          flex items-center justify-center gap-2
          rounded-lg transition-all
          ${isFullyDownloaded
            ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300 hover:bg-success-200 dark:hover:bg-success-900/40'
            : isPartiallyDownloaded
            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/40'
            : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
          }
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
        title={
          isDownloading
            ? t('App.Offline.CompactOfflineButton.TitleDownloading')
            : isFullyDownloaded
            ? t('App.Offline.CompactOfflineButton.TitleAvailableOffline')
            : isPartiallyDownloaded
            ? t('App.Offline.CompactOfflineButton.TitlePartialOffline', { downloaded: downloadedChapterCount, total: totalChapters })
            : t('App.Offline.CompactOfflineButton.TitleDownloadForOffline')
        }
        aria-label={
          isDownloading
            ? t('App.Offline.CompactOfflineButton.AriaDownloading')
            : isFullyDownloaded
            ? t('App.Offline.CompactOfflineButton.AriaAvailableOffline')
            : t('App.Offline.CompactOfflineButton.AriaDownloadForOffline')
        }
      >
        {/* Icon */}
        {isDownloading ? (
          <Loader2 className={`${iconSizeClasses[size]} animate-spin`} />
        ) : isFullyDownloaded ? (
          <CheckCircle className={iconSizeClasses[size]} />
        ) : isPartiallyDownloaded ? (
          <Download className={`${iconSizeClasses[size]} fill-current`} />
        ) : (
          <WifiOff className={iconSizeClasses[size]} />
        )}

        {/* Text (if variant is text) */}
        {variant === 'text' && (
          <span className="text-sm font-medium">
            {isDownloading
              ? t('App.Offline.CompactOfflineButton.TextDownloading')
              : isFullyDownloaded
              ? t('App.Offline.CompactOfflineButton.TextOffline')
              : isPartiallyDownloaded
              ? `${downloadedChapterCount}/${totalChapters}`
              : t('App.Offline.CompactOfflineButton.TextOffline')
            }
          </span>
        )}
      </button>

      {/* Chapter Selection Modal */}
      {isDrmEnabled && bookMetadata && (
        <ChapterSelectionModal
          bookMetadata={bookMetadata}
          chapterSelectionInfo={chapterSelectionInfo}
          availableQuota={availableChapterCount}
          quotaExceeded={quotaExceeded}
          isDrmEnabled={isDrmEnabled}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleChapterSelection}
        />
      )}

      {/* Download Progress Modal */}
      <DownloadProgressModal
        progress={downloadProgress}
        isOpen={isDownloading}
        onCancel={() => {}}
        onClose={() => {}}
      />
    </>
  );
}
